import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, GlobeAltIcon, CheckIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Country } from '../../types/UniversityPortal';

interface Session {
  id: number;
  name: string;
  code?: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

interface University {
  id: number;
  name: string;
  description?: string;
  location?: string;
  isActive: boolean;
}

interface Campus {
  id: number;
  name: string;
  universityId: number;
  city: string;
  country: string;
  isActive: boolean;
}

interface Course {
  id: number;
  name: string;
  code?: string;
  universityId: number;
  campusId?: number;
  isActive: boolean;
}

interface LeadAssociation {
  id: number;
  lead_id: number;
  lead_name: string;
  lead_email: string;
  session_id?: number;
  session_name?: string;
  session_code?: string;
  university_id: number;
  university_name: string;
  university_location?: string;
  campus_id?: number;
  campus_name?: string;
  campus_location?: string;
  course_id?: number;
  course_name?: string;
  course_code?: string;
  country_id?: number;
  country_name?: string;
  country_code?: string;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

interface EditLeadAssociationModalProps {
  isOpen: boolean;
  onClose: () => void;
  association: LeadAssociation | null;
  onSuccess: () => void;
}

const EditLeadAssociationModal: React.FC<EditLeadAssociationModalProps> = ({
  isOpen,
  onClose,
  association,
  onSuccess
}) => {
  const [step, setStep] = useState(1);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [selectedCampus, setSelectedCampus] = useState<Campus | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(true);

  // Search states
  const [sessionSearch, setSessionSearch] = useState('');
  const [universitySearch, setUniversitySearch] = useState('');
  const [campusSearch, setCampusSearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');

  // Filtered data
  const [filteredSessions, setFilteredSessions] = useState<Session[]>([]);
  const [filteredUniversities, setFilteredUniversities] = useState<University[]>([]);
  const [filteredCampuses, setFilteredCampuses] = useState<Campus[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);

  useEffect(() => {
    if (isOpen && association) {
      fetchSessions();
      fetchUniversities();
      fetchCountries();
      initializeForm();
    }
  }, [isOpen, association]);

  useEffect(() => {
    if (selectedUniversity) {
      fetchCampuses(selectedUniversity.id);
    }
  }, [selectedUniversity]);

  useEffect(() => {
    if (selectedCampus) {
      fetchCourses(selectedCampus.universityId);
    }
  }, [selectedCampus]);

  // Filter sessions
  useEffect(() => {
    const filtered = sessions.filter(session =>
      session.name.toLowerCase().includes(sessionSearch.toLowerCase()) ||
      (session.code && session.code.toLowerCase().includes(sessionSearch.toLowerCase()))
    );
    setFilteredSessions(filtered);
  }, [sessions, sessionSearch]);

  // Filter universities
  useEffect(() => {
    const filtered = universities.filter(university =>
      university.name.toLowerCase().includes(universitySearch.toLowerCase()) ||
      (university.description && university.description.toLowerCase().includes(universitySearch.toLowerCase()))
    );
    setFilteredUniversities(filtered);
  }, [universities, universitySearch]);

  // Filter campuses
  useEffect(() => {
    const filtered = campuses.filter(campus =>
      campus.name.toLowerCase().includes(campusSearch.toLowerCase()) ||
      campus.city.toLowerCase().includes(campusSearch.toLowerCase()) ||
      campus.country.toLowerCase().includes(campusSearch.toLowerCase())
    );
    setFilteredCampuses(filtered);
  }, [campuses, campusSearch]);

  // Filter courses
  useEffect(() => {
    const filtered = courses.filter(course =>
      course.name.toLowerCase().includes(courseSearch.toLowerCase()) ||
      (course.code && course.code.toLowerCase().includes(courseSearch.toLowerCase()))
    );
    setFilteredCourses(filtered);
  }, [courses, courseSearch]);

  // Filter countries
  useEffect(() => {
    const filtered = countries.filter(country =>
      country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      (country.code && country.code.toLowerCase().includes(countrySearch.toLowerCase()))
    );
    setFilteredCountries(filtered);
  }, [countries, countrySearch]);

  const initializeForm = async () => {
    if (!association) return;

    try {
      setLoading(true);

      // Set isActive from association (default to true if not set)
      setIsActive(association.is_active !== undefined ? association.is_active : true);

      // Find and set current selections
      const currentSession = sessions.find(s => s.id === association.session_id);
      const currentUniversity = universities.find(u => u.id === association.university_id);
      
      if (currentSession) {
        setSelectedSession(currentSession);
      }
      if (currentUniversity) {
        setSelectedUniversity(currentUniversity);
        await fetchCampuses(currentUniversity.id);
        
        if (association.campus_id) {
          const currentCampus = campuses.find(c => c.id === association.campus_id);
          if (currentCampus) {
            setSelectedCampus(currentCampus);
            await fetchCourses(currentCampus.universityId);
            
            if (association.course_id) {
              const currentCourse = courses.find(co => co.id === association.course_id);
              if (currentCourse) {
                setSelectedCourse(currentCourse);
              }
            }
          }
        }
      }

      // Set country if it exists (after countries are loaded)
      if (association.country_id && countries.length > 0) {
        const currentCountry = countries.find(cnt => cnt.id === association.country_id);
        if (currentCountry) {
          setSelectedCountry(currentCountry);
        }
      }
    } catch (err) {
      console.error('Error initializing form:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await api.get('/university-portal/sessions');
      if (response.data.success) {
        setSessions(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  };

  const fetchUniversities = async () => {
    try {
      const response = await api.get('/university-portal/universities');
      if (response.data.success) {
        setUniversities(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching universities:', err);
    }
  };

  const fetchCampuses = async (universityId: number) => {
    try {
      const response = await api.get(`/university-portal/universities/${universityId}/campuses`);
      if (response.data.success) {
        setCampuses(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching campuses:', err);
    }
  };

  const fetchCourses = async (universityId: number) => {
    try {
      const response = await api.get(`/university-portal/universities/${universityId}/courses`);
      if (response.data.success) {
        setCourses(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await api.get('/university-portal/countries');
      if (response.data.success) {
        setCountries(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching countries:', err);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedSession(null);
    setSelectedUniversity(null);
    setSelectedCampus(null);
    setSelectedCourse(null);
    setSelectedCountry(null);
    setSessionSearch('');
    setUniversitySearch('');
    setCampusSearch('');
    setCourseSearch('');
    setCountrySearch('');
    setError(null);
  };

  const handleCountrySelect = (country: Country | null) => {
    setSelectedCountry(country);
    setStep(2);
  };

  const handleSessionSelect = (session: Session) => {
    setSelectedSession(session);
    setStep(3);
  };

  const handleUniversitySelect = (university: University) => {
    setSelectedUniversity(university);
    setSelectedCampus(null);
    setSelectedCourse(null);
    setStep(4);
  };

  const handleCampusSelect = (campus: Campus) => {
    setSelectedCampus(campus);
    setSelectedCourse(null);
    setStep(5);
  };

  const handleCourseSelect = (course: Course) => {
    setSelectedCourse(course);
  };

  const handleSubmit = async () => {
    if (!selectedUniversity || !association) {
      setError('Please select a university');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const updateData = {
        sessionId: selectedSession?.id || null,
        universityId: selectedUniversity.id,
        campusId: selectedCampus?.id || null,
        courseId: selectedCourse?.id || null,
        countryId: selectedCountry?.id || null,
        isActive: isActive
      };

      await api.put(`/lead-associations/${association.id}`, updateData);
      
      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update association');
    } finally {
      setSubmitting(false);
    }
  };

  const getAssociationType = () => {
    if (selectedCourse) return 'Course';
    if (selectedCampus) return 'Campus';
    if (selectedSession) return 'Session';
    return 'University';
  };

  const getAssociationPath = () => {
    let path = '';
    if (selectedSession) path += `${selectedSession.name} → `;
    path += selectedUniversity?.name || '';
    if (selectedCampus) path += ` → ${selectedCampus.name}`;
    if (selectedCourse) path += ` → ${selectedCourse.name}`;
    return path;
  };

  if (!isOpen || !association) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Association</h2>
            <p className="text-sm text-gray-600 mt-1">
              Update association for {association.lead_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            {[1, 2, 3, 4, 5].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= stepNumber
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {stepNumber}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  step >= stepNumber ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  {stepNumber === 1 && 'Country'}
                  {stepNumber === 2 && 'Session'}
                  {stepNumber === 3 && 'University'}
                  {stepNumber === 4 && 'Campus'}
                  {stepNumber === 5 && 'Course'}
                </span>
                {stepNumber < 5 && (
                  <div className={`w-8 h-0.5 ml-4 ${
                    step > stepNumber ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Step 1: Country Selection */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Country (Optional)</h3>
              <p className="text-sm text-gray-600 mb-4">
                You can optionally associate a country with this lead, or skip this step.
              </p>
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <button
                  onClick={() => {
                    handleCountrySelect(null);
                  }}
                  className={`w-full text-left p-3 rounded-lg border ${
                    !selectedCountry
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center">
                    <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <div className="font-medium text-gray-900">No Country</div>
                      <div className="text-sm text-gray-500">Skip country selection</div>
                    </div>
                  </div>
                </button>
                {filteredCountries.map((country) => (
                  <button
                    key={country.id}
                    onClick={() => handleCountrySelect(country)}
                    className={`w-full text-left p-3 rounded-lg border ${
                      selectedCountry?.id === country.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <GlobeAltIcon className="h-5 w-5 text-teal-600 mr-3" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{country.name}</div>
                        {country.code && (
                          <div className="text-sm text-gray-500">Code: {country.code}</div>
                        )}
                      </div>
                      {selectedCountry?.id === country.id && (
                        <CheckIcon className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {filteredCountries.length === 0 && (
                <p className="text-gray-500 text-center py-8">No countries found</p>
              )}
            </div>
          )}

          {/* Step 2: Session Selection */}
          {step === 2 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Session (Optional)</h3>
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search sessions..."
                  value={sessionSearch}
                  onChange={(e) => setSessionSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <button
                  onClick={() => {
                    setSelectedSession(null);
                    setStep(3);
                  }}
                  className={`w-full text-left p-3 rounded-lg border ${
                    !selectedSession
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium text-gray-900">No Session</div>
                  <div className="text-sm text-gray-500">Skip session selection</div>
                </button>
                {filteredSessions.map((session) => (
                  <button
                    key={session.id}
                    onClick={() => handleSessionSelect(session)}
                    className={`w-full text-left p-3 rounded-lg border ${
                      selectedSession?.id === session.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{session.name}</div>
                    {session.code && (
                      <div className="text-sm text-gray-500">Code: {session.code}</div>
                    )}
                    <div className="text-sm text-gray-500">
                      {new Date(session.startDate).toLocaleDateString()} - {new Date(session.endDate).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Session Selection */}
          {step === 2 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Session (Optional)</h3>
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search sessions..."
                  value={sessionSearch}
                  onChange={(e) => setSessionSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <button
                  onClick={() => {
                    setSelectedSession(null);
                    setStep(3);
                  }}
                  className={`w-full text-left p-3 rounded-lg border ${
                    !selectedSession
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium text-gray-900">No Session</div>
                  <div className="text-sm text-gray-500">Skip session selection</div>
                </button>
                {filteredSessions.map((session) => (
                  <button
                    key={session.id}
                    onClick={() => handleSessionSelect(session)}
                    className={`w-full text-left p-3 rounded-lg border ${
                      selectedSession?.id === session.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{session.name}</div>
                    {session.code && (
                      <div className="text-sm text-gray-500">Code: {session.code}</div>
                    )}
                    <div className="text-sm text-gray-500">
                      {new Date(session.startDate).toLocaleDateString()} - {new Date(session.endDate).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: University Selection */}
          {step === 3 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select University</h3>
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search universities..."
                  value={universitySearch}
                  onChange={(e) => setUniversitySearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredUniversities.map((university) => (
                  <button
                    key={university.id}
                    onClick={() => handleUniversitySelect(university)}
                    className={`w-full text-left p-3 rounded-lg border ${
                      selectedUniversity?.id === university.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{university.name}</div>
                    {university.description && (
                      <div className="text-sm text-gray-500">{university.description}</div>
                    )}
                    {university.location && (
                      <div className="text-sm text-gray-500">{university.location}</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Campus Selection */}
          {step === 4 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Campus (Optional)</h3>
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search campuses..."
                  value={campusSearch}
                  onChange={(e) => setCampusSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <button
                  onClick={() => {
                    setSelectedCampus(null);
                    setStep(5);
                  }}
                  className={`w-full text-left p-3 rounded-lg border ${
                    !selectedCampus
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium text-gray-900">No Campus</div>
                  <div className="text-sm text-gray-500">Skip campus selection</div>
                </button>
                {filteredCampuses.map((campus) => (
                  <button
                    key={campus.id}
                    onClick={() => handleCampusSelect(campus)}
                    className={`w-full text-left p-3 rounded-lg border ${
                      selectedCampus?.id === campus.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{campus.name}</div>
                    <div className="text-sm text-gray-500">{campus.city}, {campus.country}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Course Selection */}
          {step === 5 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Course (Optional)</h3>
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={courseSearch}
                  onChange={(e) => setCourseSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <button
                  onClick={() => {
                    setSelectedCourse(null);
                  }}
                  className={`w-full text-left p-3 rounded-lg border ${
                    !selectedCourse
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium text-gray-900">No Course</div>
                  <div className="text-sm text-gray-500">Skip course selection</div>
                </button>
                {filteredCourses.map((course) => (
                  <button
                    key={course.id}
                    onClick={() => handleCourseSelect(course)}
                    className={`w-full text-left p-3 rounded-lg border ${
                      selectedCourse?.id === course.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{course.name}</div>
                    {course.code && (
                      <div className="text-sm text-gray-500">Code: {course.code}</div>
                    )}
                  </button>
                ))}
              </div>

              {/* Status Selection */}
              <div className="mt-6 p-4 border border-gray-200 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Association Status
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="isActive-edit"
                      value="true"
                      checked={isActive === true}
                      onChange={() => setIsActive(true)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="isActive-edit"
                      value="false"
                      checked={isActive === false}
                      onChange={() => setIsActive(false)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Select whether this association should be active or inactive
                </p>
              </div>
            </div>
          )}

          {/* Summary */}
          {(selectedUniversity || selectedSession) && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Association Summary</h4>
              <div className="text-sm text-gray-600">
                <div><strong>Lead:</strong> {association.lead_name}</div>
                <div><strong>Type:</strong> {getAssociationType()}</div>
                <div><strong>Path:</strong> {getAssociationPath()}</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="flex space-x-3">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Previous
              </button>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            {step < 5 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 3 && !selectedUniversity}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting || !selectedUniversity}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Updating...' : 'Update Association'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditLeadAssociationModal;
